import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { gatewaysApi, clientsApi } from "@/lib/apiClient";
import { PageContainer, PageHeader } from "@/components/Layout";
import { Mono, CodePill, StatusDot } from "@/components/Atoms";
import { ArrowLeft, Pencil, AlertTriangle, ArrowUpRight, Activity, Radio } from "lucide-react";
import { toast } from "sonner";

export default function GatewayDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  const list = useQuery({ queryKey: ["gateways"], queryFn: () => gatewaysApi.list() });
  const hw = useQuery({ queryKey: ["gw-hw"], queryFn: gatewaysApi.hwList });
  const clients = useQuery({ queryKey: ["clients"], queryFn: clientsApi.list });

  const gw = list.data?.find((g) => g.id === id);

  // Authoritative status from backend (polled every 30s).
  const status = useQuery({
    queryKey: ["gw-status", gw?.mac],
    queryFn: () => gatewaysApi.status(gw.mac),
    enabled: !!gw?.mac,
    refetchInterval: 30000,
    retry: 0,
  });

  // Recent messages from this gateway.
  const messages = useQuery({
    queryKey: ["gw-messages", gw?.mac],
    queryFn: () => gatewaysApi.messages({ gw_mac: gw.mac }),
    enabled: !!gw?.mac,
    retry: 0,
  });

  const updateM = useMutation({
    mutationFn: ({ id, data }) => gatewaysApi.update(id, data),
    onSuccess: () => {
      toast.success("Gateway updated");
      qc.invalidateQueries({ queryKey: ["gateways"] });
      setEditing(false);
    },
  });

  if (list.isLoading) {
    return <PageContainer><p className="text-sm text-zinc-500">Loading…</p></PageContainer>;
  }

  if (!gw) {
    return (
      <PageContainer>
        <button onClick={() => navigate("/gateways")} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 mb-3" data-testid="gw-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Gateways
        </button>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 max-w-xl">
          <div className="flex items-center gap-2 text-amber-700 mb-2">
            <AlertTriangle className="w-4 h-4" />
            <div className="font-heading font-semibold">Gateway not found</div>
          </div>
          <p className="text-sm text-amber-900">
            No gateway with id <Mono>{id}</Mono> was returned by the backend.
          </p>
        </div>
      </PageContainer>
    );
  }

  const client = clients.data?.find((c) => c.id === gw.client_id);
  const hwModel = hw.data?.find((h) => h.id === gw.hw_id);
  const online = status.data?.online ?? false;
  const lastSeen = status.data?.last_seen || gw.last_seen;

  return (
    <PageContainer>
      <button onClick={() => navigate("/gateways")} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 mb-3" data-testid="gw-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Gateways
      </button>
      <PageHeader
        title={gw.name}
        subtitle={`${client?.name || "—"} · ${hwModel?.canonical_hw || "—"}`}
        actions={
          <button
            data-testid="gw-edit-open"
            onClick={() => setEditing(true)}
            className="bg-[#3730A3] text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-indigo-900 inline-flex items-center gap-1.5"
          >
            <Pencil className="w-4 h-4" /> Edit
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Info label="MAC" value={<Mono>{gw.mac}</Mono>} testid="gw-info-mac" />
        <Info
          label="Status"
          testid="gw-info-status"
          value={
            <span className="inline-flex items-center gap-2">
              <StatusDot online={online} label={online ? "online" : "offline"} />
              {status.isError && <span className="text-[10px] text-amber-600 font-mono">status endpoint unavailable</span>}
            </span>
          }
        />
        <Info
          label="Last seen"
          testid="gw-info-last-seen"
          value={<span className="font-mono text-xs">{lastSeen ? new Date(lastSeen).toLocaleString() : "—"}</span>}
        />
        <Info label="Hardware" value={<CodePill>{hwModel?.canonical_hw || gw.hw_id || "—"}</CodePill>} />
        <Info label="Vendor" value={hwModel?.vendor || "—"} />
        <Info label="Protocol" value={<CodePill>{hwModel?.protocol || "—"}</CodePill>} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Link
          to={`/gateways?client=${gw.client_id}`}
          className="bg-white border border-zinc-200 rounded-lg p-5 hover:border-zinc-900 hover:shadow-sm transition group"
        >
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Client</div>
            <ArrowUpRight className="w-3.5 h-3.5 text-zinc-400 group-hover:text-zinc-900 transition" />
          </div>
          <div className="font-heading text-lg font-semibold mt-2">{client?.name || "—"}</div>
        </Link>

        <div className="bg-white border border-zinc-200 rounded-lg p-5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2 inline-flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5" /> Messages (visible)
          </div>
          <div className="font-heading text-3xl font-bold tracking-tight">
            {messages.data?.length ?? "—"}
          </div>
          <div className="text-[10px] text-zinc-500 font-mono mt-1">
            from this gateway in the live buffer
          </div>
        </div>

        <Link
          to="/envelope-rules"
          className="bg-white border border-zinc-200 rounded-lg p-5 hover:border-zinc-900 hover:shadow-sm transition group"
        >
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono inline-flex items-center gap-1.5">
              <Radio className="w-3.5 h-3.5" /> Envelope routes
            </div>
            <ArrowUpRight className="w-3.5 h-3.5 text-zinc-400 group-hover:text-zinc-900 transition" />
          </div>
          <div className="font-heading text-base font-semibold mt-2">
            HW: {hwModel?.canonical_hw || "—"}
          </div>
          <div className="text-[10px] text-zinc-500 font-mono mt-1">
            view routes mapped to this hardware
          </div>
        </Link>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">recent messages</div>
          <div className="font-heading text-base font-semibold mt-0.5">Live feed from {gw.name}</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left p-3 font-semibold">Received</th>
              <th className="text-left p-3 font-semibold">Flag</th>
              <th className="text-left p-3 font-semibold">Payload</th>
            </tr>
          </thead>
          <tbody>
            {messages.data?.slice(0, 15).map((m) => (
              <tr key={m.id} className="border-t border-zinc-100">
                <td className="p-3 text-xs font-mono text-zinc-500">{new Date(m.received_at).toLocaleString()}</td>
                <td className="p-3"><CodePill>{m.flag}</CodePill></td>
                <td className="p-3"><Mono className="text-zinc-600 truncate block max-w-[600px]">{m.payload_hex}</Mono></td>
              </tr>
            ))}
            {!messages.data?.length && (
              <tr><td colSpan={3} className="py-8 text-center text-sm text-zinc-500">No messages yet for this gateway.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditGatewayModal
          gateway={gw}
          clients={clients.data || []}
          hwList={hw.data || []}
          onClose={() => setEditing(false)}
          onSave={(form) => updateM.mutate({ id: gw.id, data: form })}
        />
      )}
    </PageContainer>
  );
}

function Info({ label, value, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className="mt-1.5 text-sm">{value}</div>
    </div>
  );
}

function EditGatewayModal({ gateway, clients, hwList, onClose, onSave }) {
  const [form, setForm] = useState({ ...gateway });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" onClick={onClose}>
      <div className="bg-white border border-zinc-200 rounded-lg w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading text-lg font-bold mb-3">Edit gateway</h2>
        <form
          onSubmit={(e) => { e.preventDefault(); onSave(form); }}
          className="space-y-3"
          data-testid="gw-edit-form"
        >
          {[["name","Name"], ["mac","MAC"]].map(([k, label]) => (
            <label key={k} className="block">
              <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
              <input
                data-testid={`gw-edit-${k}`}
                className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm"
                value={form[k] || ""}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              />
            </label>
          ))}
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Client</span>
            <select
              value={form.client_id || ""}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
            >
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Hardware</span>
            <select
              value={form.hw_id || ""}
              onChange={(e) => setForm({ ...form, hw_id: e.target.value })}
              className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
            >
              {hwList.map((h) => <option key={h.id} value={h.id}>{h.canonical_hw} · {h.name}</option>)}
            </select>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm">Cancel</button>
            <button data-testid="gw-edit-save" type="submit" className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}
